// @givmo/design-system — Icon (ports Flutter `CIcon`, images/icon.dart).
//
// The Flutter kit rendered custom/material SVG assets via `SvgPicture.asset`. On web we
// back icons with `lucide-react` (the locked icon library). A Lucide icon component is
// passed in as `icon`; the wrapper applies the kit's defaults: 20px default size,
// scale-down fit, optional `color` (maps to SVG `currentColor`).
//
// ICON-MAPPING NOTE (for later slices 03-3b/c/d): the Flutter kit referenced bespoke SVGs
// in `GivmoSvgIcons` and payment-brand glyphs in `GivmoPaymentIcons`. Where a Lucide
// equivalent exists (chevrons, info, eye, check, x …) use Lucide. The custom/brand SVGs
// (logo marks, payment-card brands) have no Lucide match and must be ported as raw SVG
// assets in a later slice; this component intentionally only covers the Lucide-backed path.
import type { ComponentType, SVGProps } from 'react';
import { cn } from '../lib/utils';
import { testIdProp, type TestIdProps } from '../lib/testid';

/** A Lucide icon component (e.g. `import { ChevronRight } from 'lucide-react'`). */
export type LucideLikeIcon = ComponentType<SVGProps<SVGSVGElement> & { size?: number | string }>;

export interface IconProps extends TestIdProps {
  /** The Lucide (or Lucide-compatible) icon component to render. */
  icon: LucideLikeIcon;
  /** Square size in px. Defaults to 20 (Flutter CIcon.defaultSize). */
  size?: number;
  /** Stroke/fill color. Defaults to `currentColor` (inherits text color). */
  color?: string;
  className?: string;
  'aria-label'?: string;
  'aria-hidden'?: boolean;
}

/** Flutter CIcon.defaultSize. */
export const ICON_DEFAULT_SIZE = 20;

export function Icon({
  icon: IconComponent,
  size = ICON_DEFAULT_SIZE,
  color = 'currentColor',
  className,
  testId,
  'aria-label': ariaLabel,
  'aria-hidden': ariaHidden,
}: IconProps) {
  return (
    <IconComponent
      width={size}
      height={size}
      size={size}
      color={color}
      className={cn('inline-block shrink-0 align-middle', className)}
      aria-label={ariaLabel}
      aria-hidden={ariaHidden ?? (ariaLabel ? undefined : true)}
      role={ariaLabel ? 'img' : undefined}
      {...testIdProp(testId)}
    />
  );
}
